import {BranchName, NavList, Link} from '@primer/react'
import {PlusIcon, DotIcon} from '@primer/octicons-react'
import {BetaLabel} from '@github-ui/lifecycle-labels/beta'
import {FeedbackDialog, type FeedbackDialogRef} from '@github-ui/feedback-dialog'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import {usePageDataUrl} from '@github-ui/pull-request-page-data-tooling/use-page-data-url'
import {CommandKeybindingHint} from '@github-ui/ui-commands'
import {useRef, type Ref} from 'react'
import type {StackData} from '../page-data/payloads/header'
import {MAX_STACK_SIZE, STACKS_FEEDBACK_SUBJECT} from '../helpers/constants'
import {StateIcon} from './StateIcon'
import styles from './StackState.module.css'

type StackOverlayProps = {
  pullRequestNumber: number
  stack: StackData
  baseBranchText: string
  addToStackURL?: string
  showShortcutGuidance: boolean
  tabSuffix?: string
  showStackMutationButtons?: boolean
  // Attached to the current entry so the overlay's focus trap focuses it on open,
  // which scrolls it into view within a long stack.
  currentEntryRef?: Ref<HTMLAnchorElement>
  onSwitchBranch?: (targetPullRequestId: number, targetPositionInStack: number) => void
  onAddToStackClick?: () => void
}

export function StackOverlay({
  pullRequestNumber,
  stack,
  baseBranchText,
  addToStackURL,
  showShortcutGuidance,
  tabSuffix = '',
  showStackMutationButtons = true,
  currentEntryRef,
  onSwitchBranch,
  onAddToStackClick,
}: StackOverlayProps) {
  const {pulls, allPullsLocked} = stack
  const isStackAtCapacity = stack.size === MAX_STACK_SIZE
  const shouldShowStackMutationButtons = showStackMutationButtons && addToStackURL && !allPullsLocked
  const feedbackRef = useRef<FeedbackDialogRef>(null)
  const feedbackLinkRef = useRef<HTMLButtonElement>(null)
  const feedbackEndpoint = usePageDataUrl(PageData.submitFeatureFeedback)
  const feedbackDialogEnabled = isFeatureEnabled('pull_request_stacks_feedback_dialog')

  return (
    <>
      <NavList className={styles.stackNavList}>
        {shouldShowStackMutationButtons && (
          <NavList.Item
            className={styles.addToStackItem}
            href={addToStackURL}
            inactiveText={isStackAtCapacity ? 'Maximum stack size reached' : undefined}
            onClick={() => {
              onAddToStackClick?.()
            }}
          >
            <NavList.LeadingVisual>
              <PlusIcon />
            </NavList.LeadingVisual>
            Add to stack
          </NavList.Item>
        )}
        {pulls.map((stackedPR, index) => {
          const isCurrent = stackedPR.number === pullRequestNumber
          return (
            <NavList.Item
              key={`stacked-pr-${stackedPR.number}`}
              ref={isCurrent ? currentEntryRef : undefined}
              href={`${stackedPR.url}${tabSuffix}`}
              aria-current={isCurrent ? 'page' : undefined}
              onClick={() => {
                if (isCurrent) return
                onSwitchBranch?.(stackedPR.number, index + 1)
              }}
            >
              <NavList.LeadingVisual>
                <StateIcon state={stackedPR.state} />
              </NavList.LeadingVisual>
              {stackedPR.title}
              <NavList.Description variant="block">
                #{stackedPR.number} · {stackedPR.headBranch}
              </NavList.Description>
            </NavList.Item>
          )
        })}
        <li className={styles.branchItem}>
          <span className={styles.branchItemContent}>
            <DotIcon className="fgColor-muted" />
            <BranchName as="span">{baseBranchText}</BranchName>
          </span>
        </li>
      </NavList>
      <div className={styles.previewFooter}>
        {showShortcutGuidance && (
          <span className={styles.shortcutGuidance}>
            Use <CommandKeybindingHint commandId="pull-requests:navigate-upstack" />{' '}
            <CommandKeybindingHint commandId="pull-requests:navigate-downstack" /> to navigate up/down the stack
          </span>
        )}
        {feedbackDialogEnabled ? (
          <>
            <BetaLabel />
            <Link
              ref={feedbackLinkRef}
              as="button"
              type="button"
              inline
              className="f6"
              aria-label="Give feedback on stacks"
              onClick={() => feedbackRef.current?.openDialog()}
            >
              Give feedback
            </Link>
            <FeedbackDialog
              ref={feedbackRef}
              subject={STACKS_FEEDBACK_SUBJECT}
              hideButton
              endpoint={feedbackEndpoint}
              returnFocusRef={feedbackLinkRef}
            />
          </>
        ) : (
          <BetaLabel feedbackUrl="https://gh.io/stacks-feedback" opensInNewTab />
        )}
      </div>
    </>
  )
}
